import React from 'react';
import { getToken, useTheme } from '../../../utils/themeTokens';
import { useI18n } from '../../../i18n';
import { colorTokenForClass, formatConfidence, formatDuration, summariseByClass } from '../utils/predictionView';

/**
 * What the model said about the image on screen.
 *
 * Classification and detection are genuinely different answers and get
 * different panels: a classifier produces ONE answer with a probability
 * distribution behind it, a detector produces N findings each with its own
 * confidence. Rendering the second as a ranked list of "classes" was the old
 * screen's shape, and it could not say "three people and one helmet" at all.
 */

const Row = ({ label, value }) => (
  <div className="lf-predict-meta__row">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const PredictionResult = ({ image, detections, conf, selected, highlighted, onHighlight }) => {
  const { t } = useI18n();
  const theme = useTheme();
  // eslint-disable-next-line no-unused-vars
  const _themeTick = theme; // getToken below must re-read when the theme flips

  if (!image) {
    return (
      <div className="lf-predict-panel lf-predict-panel--right">
        <div className="lf-predict-empty">
          <i className="fas fa-chart-pie" aria-hidden="true"></i>
          <p className="lf-predict-empty__title">{t('prediction.result.idle')}</p>
        </div>
      </div>
    );
  }

  if (image.status === 'failed') {
    return (
      <div className="lf-predict-panel lf-predict-panel--right">
        <div className="lf-predict-panel__head">
          <h2 className="lf-predict-panel__title">{t('prediction.result.failedTitle')}</h2>
        </div>
        <div className="lf-predict-panel__body">
          <p className="lf-predict-inline-error">
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {image.error}
          </p>
        </div>
      </div>
    );
  }

  if (image.status !== 'done') {
    return (
      <div className="lf-predict-panel lf-predict-panel--right">
        <div className="lf-predict-empty">
          <i className={`fas ${image.status === 'running' ? 'fa-spinner fa-spin' : 'fa-clock'}`} aria-hidden="true"></i>
          <p className="lf-predict-empty__title">
            {t(image.status === 'running' ? 'prediction.result.running' : 'prediction.result.pending')}
          </p>
        </div>
      </div>
    );
  }

  const result = image.result || {};
  const isClassification = result.task === 'classification';
  const top = detections[0] || null;
  const summary = summariseByClass(detections);
  const colorOf = (classId) => getToken(colorTokenForClass(classId), '#10b981');

  return (
    <div className="lf-predict-panel lf-predict-panel--right">
      <div className="lf-predict-panel__head">
        <h2 className="lf-predict-panel__title">{t('prediction.result.title')}</h2>
      </div>

      <div className="lf-predict-panel__body">
        {!detections.length ? (
          // A detector finding nothing above the slider is a real, useful answer
          // — and a different one from a failure. Saying so beats an empty box
          // the user reads as broken.
          <div className="lf-predict-empty">
            <i className="fas fa-search-minus" aria-hidden="true"></i>
            <p className="lf-predict-empty__title">{t('prediction.result.nothing')}</p>
            <p className="lf-predict-empty__hint">
              {t('prediction.result.nothingHint', { conf: Math.round(conf * 100) })}
            </p>
          </div>
        ) : (
          <>
            {isClassification && top && (
              <div className="lf-predict-top">
                <span className="lf-predict-top__label">{t('prediction.result.predictedClass')}</span>
                <span className="lf-predict-top__class">{top.class_name}</span>
                <span className="lf-predict-top__conf">
                  {t('prediction.result.confident', { value: formatConfidence(top.confidence) })}
                </span>
              </div>
            )}

            {isClassification ? (
              <div className="lf-predict-bars">
                <h3 className="lf-predict-subhead">{t('prediction.result.allClasses')}</h3>
                {detections.map((detection) => (
                  <div key={detection.class_id} className="lf-predict-bar">
                    <div className="lf-predict-bar__head">
                      <span>{detection.class_name}</span>
                      <span>{formatConfidence(detection.confidence)}</span>
                    </div>
                    <div className="lf-predict-bar__track">
                      <div
                        className="lf-predict-bar__fill"
                        style={{
                          // Absolute, not relative to the top score: a bar
                          // normalised to its own maximum draws the winner at
                          // 100% whether it scored 0.99 or 0.34, which is the
                          // one thing the user is reading it for.
                          width: `${Math.max((detection.confidence ?? 0) * 100, 1)}%`,
                          background: colorOf(detection.class_id),
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="lf-predict-summary">
                  <h3 className="lf-predict-subhead">
                    {t('prediction.result.found', { count: detections.length })}
                  </h3>
                  <div className="lf-predict-summary__chips">
                    {summary.map((bucket) => (
                      <span key={bucket.class_id} className="lf-predict-chip lf-predict-chip--count">
                        <span
                          className="lf-predict-swatch"
                          style={{ background: colorOf(bucket.class_id) }}
                          aria-hidden="true"
                        />
                        {bucket.class_name} × {bucket.count}
                      </span>
                    ))}
                  </div>
                </div>

                <ul className="lf-predict-detections">
                  {detections.map((detection, index) => (
                    <li key={`${detection.class_id}-${index}`}>
                      <button
                        type="button"
                        className={`lf-predict-detection${highlighted === index ? ' is-highlighted' : ''}`}
                        onMouseEnter={() => onHighlight(index)}
                        onMouseLeave={() => onHighlight(null)}
                        onFocus={() => onHighlight(index)}
                        onBlur={() => onHighlight(null)}
                      >
                        <span
                          className="lf-predict-swatch"
                          style={{ background: colorOf(detection.class_id) }}
                          aria-hidden="true"
                        />
                        <span className="lf-predict-detection__name">{detection.class_name}</span>
                        <span className="lf-predict-detection__conf">{formatConfidence(detection.confidence)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}

        <div className="lf-predict-meta">
          <Row label={t('prediction.result.image')} value={image.name} />
          <Row label={t('prediction.result.model')} value={selected?.model_name || '-'} />
          <Row label={t('prediction.result.took')} value={formatDuration(result.inference_ms)} />
          <Row
            label={t('prediction.result.size')}
            value={result.image ? `${result.image.width} × ${result.image.height}` : '-'}
          />
        </div>
      </div>
    </div>
  );
};

export default PredictionResult;
